import { ReactElement, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import HotTable, { HotTableProps } from "@handsontable/react";
import { LICENSES } from "../../../constants/Licenses";
import "./styles/vz-hot-table.scss";
import { toast } from "react-toastify";
import { VZHotTableConfig } from "./vz-hot-table-config";
import Handsontable from "handsontable";
import { defaultSummaryColumnFunction, getSummaryValue, vzHotTableCells } from "./vz-hot-table-helpers";
import VZHotTableControls from "./VZHotTableControls";
import {AuthContext} from "../../../contexts/AuthContext";
import VZAlertNoUnitTypes from "../vz-alert/prebuilts/VZAlertNoUnitTypes";
import {userIsAdmin} from "../../../authorization/AuthorizationCheckers";


export type TVZHotTableRowType = "summary" | "historical" | "generic" | "child";
export type TVZHotTableSummaryFunction = "sum" | "avg" | ((row: number, col: number, values: number[]) => number | null);

export interface IVZHotTableProps {
    data: IVZHotTableRow[] | undefined;
    colWidths: (number | string | undefined)[];
    colHeaders: string[];
    numRowHeaderCols?: number;
    fixedRowsTop?: number;
    fixedRowsBottom?: number;
    handsontableProps?: Pick<HotTableProps, "columns">;
    tableConfig?: VZHotTableConfig;
    isDataLoading?: boolean;
    summaryColumns?: {
        col: number,
        summaryFunction: TVZHotTableSummaryFunction,
    }[];
    isSearchable?: boolean;
    isUndoRedoable?: boolean;
    canSuppressZeroes?: boolean;
    // the onChange callback has a .5s debounce, and only emits values that were actually changed
    onChange?: (changes: [number, number, number][]) => void;
    // anything passed to customControls will be rendered inside the VZHotTableControls header
    customControls?: ReactNode;
}

export interface IVZHotTableRow {
    label: string;
    rowType?: TVZHotTableRowType;
    rowId?: string;
    parentRowId?: string;
    values: number[];
    // parentRowSummaryFunction only used by rows of rowType summary, defines how child rows should roll up to the parent row
    parentRowSummaryFunction?: TVZHotTableSummaryFunction,
    rowSummaryFunction?: TVZHotTableSummaryFunction;
    extraData?: {
        [key: string]: string | number | null | undefined;
    }
}

const undoRedoSkipSources = ["loadData", "updateData", "computedRowSummary", "computedColSummary"];
const userEntryInputSources = ["edit", "CopyPaste.paste", "Autofill.fill"];
const undoRedoSources = ["UndoRedo.undo", "UndoRedo.redo"];
const calculationInputSources = ["computedColSummary"];

// While primarily built with functionality needed to handle the revenue screens, this component allows you to pass through as much or as little
// of the Handsontable api props as you want, so it should (hopefully) be easily usable for generic tables, giving you access to the styling and
// convenience methods of the VZHotTableConfig class
export function VZHotTable({
    data,
    colWidths,
    colHeaders,
    numRowHeaderCols,
    fixedRowsTop,
    fixedRowsBottom,
    handsontableProps,
    tableConfig,
    isDataLoading,
    isSearchable,
    isUndoRedoable,
    canSuppressZeroes,
    summaryColumns,
    onChange,
    customControls,
}: IVZHotTableProps): ReactElement {
    const rowHeaderCols = numRowHeaderCols === undefined ? 1 : numRowHeaderCols;
    const endCol = 12 + rowHeaderCols;

    const {user} = useContext(AuthContext);

    const hotRef = useRef<HotTable>(null);
    const [filterText, setFilterText] = useState("");
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [suppressZeroes, setSuppressZeroes] = useState(false);

    const onChangeUpdateDebounce = useRef<ReturnType<typeof setTimeout>>();
    const onChangeColorFlashTimeout = useRef<ReturnType<typeof setTimeout>>();
    const onChangeUpdatesToMake = useRef<[number, number, number][]>([]);

    const hiddenRows = useMemo<number[]>(() => {
        if (!data) {
            return [];
        }
        const rowsToHide: number[] = [];
        const allRowIndexes = data.map((_x, i) => i);
        const rowsToAlwaysHideWhenFiltering = data.filter(row => row.rowType == "summary").map(x => data.indexOf(x));

        if (filterText) {
            const filteredRowIndexes = data.filter(row => row.label.toLowerCase().includes(filterText.toLowerCase())).map(x => data.indexOf(x));
            rowsToHide.push(...allRowIndexes.filter(x => !filteredRowIndexes.includes(x)));
        }


        if (suppressZeroes) {
            const rowsWithAllZeroes = data.filter(row => row.values.every(x => !x)).map(x => data.indexOf(x));
            rowsToHide.push(...allRowIndexes.filter(x => rowsWithAllZeroes.includes(x)));
        }

        if (rowsToHide.length > 0) {
            rowsToHide.push(...rowsToAlwaysHideWhenFiltering);
        }

        return rowsToHide;
    }, [filterText, suppressZeroes]);

    useEffect(() => {
        return () => {
            if (onChangeColorFlashTimeout.current) {
                clearTimeout(onChangeColorFlashTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        const hot = hotRef.current?.hotInstance;

        if (!hot || !data) {
            return;
        }

        hot.loadData(data);

        // run column calculations on load, instead of making you manually calculate them for initial display
        const tableSummaryFunction = tableConfig?.tableSummaryFunction ?? "sum";
        const updatesToMake: [number, number, number][] = [];
        for (let colIndex = rowHeaderCols; colIndex < endCol; colIndex++) {
            const summaryRows = data.filter(x => x.rowType == "summary");

            for (const summaryRow of summaryRows) {
                const unitTypeValuesToSummarize: number[] = data.filter(x => x.parentRowId === summaryRow.rowId).map(x => x.values[colIndex - rowHeaderCols] ?? 0);
                const summaryRowIndex = data.indexOf(summaryRow);
                const summaryFunction = summaryRow.parentRowSummaryFunction ?? tableSummaryFunction;
                const summaryValue = getSummaryValue(summaryRowIndex, colIndex - rowHeaderCols, unitTypeValuesToSummarize, summaryFunction) ?? 0;
                updatesToMake.push([summaryRowIndex, colIndex, summaryValue]);
            }
        }

        if (updatesToMake.length > 0) {
            hot.setDataAtCell(updatesToMake, "computedColSummary");
        }
    }, [data]);

    function handleOnChange(changes: [number, number, number][]) {
        const hot = hotRef.current?.hotInstance;

        if (!onChange || !hot || !data) {
            return;
        }

        if (onChangeUpdateDebounce.current) {
            clearTimeout(onChangeUpdateDebounce.current);
        }

        for (const [rowIndex, colIndex, value] of changes) {
            const row = data[rowIndex];

            if (!row) {
                continue;
            }

            const existingUpdate = onChangeUpdatesToMake.current.find(x => x[0] == rowIndex && x[1] == colIndex);

            if (existingUpdate) {
                existingUpdate[2] = value;
            } else {
                onChangeUpdatesToMake.current.push([rowIndex, colIndex, value]);
            }
        }

        onChangeUpdateDebounce.current = setTimeout(() => {
            if (onChangeUpdatesToMake.current.length == 0) {
                return;
            }

            onChange(onChangeUpdatesToMake.current);
            onChangeUpdatesToMake.current = [];
        }, 500);
    }

    const compiledTableProps = useMemo<HotTableProps>(() => {
        const extraRowHeadersColumns= [];
        if(rowHeaderCols > 1) {
            for(let i = 0; i < rowHeaderCols - 1; i++) {
                extraRowHeadersColumns.push({data: `extraData.label${i + 1}`});
            }
        }
        return {
            ref: hotRef,
            width: "100%",
            height: "auto",
            stretchH: "all",
            rowHeights: 48,
            autoColumnSize: false,
            manualColumnResize: false,
            licenseKey: LICENSES.HandsOnTable,
            data: data,
            disableVisualSelection: ["header"],
            colWidths,
            colHeaders,
            fixedRowsTop,
            fixedRowsBottom,
            // @bowman - this hiddenRows typing is to get around a bug with their type declarations for Handsontable v12
            // https://github.com/handsontable/handsontable/issues/8850
            hiddenRows: {
                rows: hiddenRows,
                copyPasteEnabled: false,
            } as unknown as Handsontable.plugins.HiddenRows.Settings,
            columns: [
                {data: "label"},
                ...extraRowHeadersColumns,
                {data: `values.0`, type: "numeric"},
                {data: `values.1`, type: "numeric"},
                {data: `values.2`, type: "numeric"},
                {data: `values.3`, type: "numeric"},
                {data: `values.4`, type: "numeric"},
                {data: `values.5`, type: "numeric"},
                {data: `values.6`, type: "numeric"},
                {data: `values.7`, type: "numeric"},
                {data: `values.8`, type: "numeric"},
                {data: `values.9`, type: "numeric"},
                {data: `values.10`, type: "numeric"},
                {data: `values.11`, type: "numeric"},
                {data(row: Handsontable.RowObject) {
                    return defaultSummaryColumnFunction(data, tableConfig, row, endCol);
                }},
            ],
            cells: vzHotTableCells(tableConfig),
            beforeChange(changes, _source) {
                const source: string = _source as string;
                const hot = hotRef.current?.hotInstance;

                if (!changes || !hot || !data) {
                    return;
                }

                if ([...userEntryInputSources, ...undoRedoSources].includes(source)) {
                    let parentRowSelected = false;
                    let childRowSelected = false;
                    const allChildRowUpdatesToMake: [number, number, number][] = [];

                    for (let i = 0; i < changes.length; i++) {
                        const change = changes[i];

                        if (!change) {
                            continue;
                        }

                        const row = change[0];
                        const col = change[1];
                        const oldVal = change[2];
                        const newVal = change[3];
                        const colIndex = typeof col === "string" ? hot.propToCol(col) : col;
                        const {minValue, maxValue} = tableConfig?.getConfig(row, colIndex) ?? {};

                        if (newVal == null || newVal == undefined || newVal == "") {
                            change[3] = 0;
                        } else {
                            const newValAsNumber = Number(newVal);

                            if (isNaN(newValAsNumber)) {
                                toast.warning("Please only enter numbers.");
                                change[3] = oldVal;
                                return false;
                            }

                            if (minValue != null && newVal < minValue) {
                                toast.warning(`Values must be ${minValue} or more.`);
                                change[3] = oldVal;
                                return false;
                            }

                            if (maxValue != null && newVal > maxValue) {
                                toast.warning(`Values must be ${maxValue} or less.`);
                                change[3] = oldVal;
                                return false;
                            }
                        }

                        if (row == undefined || col == undefined || oldVal === undefined || newVal == undefined || String(oldVal) === String(change[3])) {
                            continue;
                        }

                        const rowConfig = data[row];

                        if (!rowConfig) {
                            continue;
                        }

                        if (rowConfig.rowType == "summary") {
                            parentRowSelected = true;

                            const childRows = data.filter(x => x.parentRowId == rowConfig.rowId);
                            const childRowIndexes = childRows.map(x => data.indexOf(x));
                            const childRowUpdates: [number, number, number][] = childRowIndexes.map(childRow => [childRow, colIndex, newVal]);

                            allChildRowUpdatesToMake.push(...childRowUpdates);
                        }

                        if (rowConfig.rowType == "child") {
                            childRowSelected = true;
                        }
                    }

                    if (parentRowSelected && childRowSelected) {
                        toast.warning("You cannot update parent and child rows at the same time.");
                        return false;
                    }

                    // if updating one or more summary row cells, we send that value to all of the children, instead, to maintain undo/redo state
                    if (parentRowSelected) {
                        hot.setDataAtCell(allChildRowUpdatesToMake, "edit");

                        for (const [flashRow, flashCol] of allChildRowUpdatesToMake) {
                            hot.setCellMeta(flashRow, flashCol, "className", "cell-flash");
                        }

                        onChangeColorFlashTimeout.current = setTimeout(() => {
                            if (!hot || hot.isDestroyed) {
                                return;
                            }

                            for (const [flashRow, flashCol] of allChildRowUpdatesToMake) {
                                if (hot.getCell(flashRow, flashCol) == null) {
                                    continue;
                                }
                                hot.removeCellMeta(flashRow, flashCol, "className");
                            }
                            hot.render();
                        }, 1000);
                        return false;
                    }
                }
            },
            afterChange: (changes, _source) => {
                const source: string = _source as string;
                const hot = hotRef.current?.hotInstance;

                if (!changes || !hot || ![...userEntryInputSources, ...undoRedoSources, ...calculationInputSources].includes(source) || !data) {
                    return;
                }

                const colsToSummarize = new Set<number>();
                const rowsToSummarize = new Set<number>();
                const tableSummaryFunction = tableConfig?.tableSummaryFunction ?? "sum";

                hot.batch(() => {
                    if ([...userEntryInputSources, ...undoRedoSources].includes(source)) {
                        const changedData: [number, number, number][] = [];

                        for (let i = 0; i < changes.length; i++) {
                            const change = changes[i];

                            if (!change) {
                                continue;
                            }

                            const row = change[0];
                            const col = change[1];
                            const oldVal = change[2];
                            const newVal = change[3];

                            if (row == undefined || col == undefined || oldVal === undefined || newVal == undefined || oldVal === newVal) {
                                continue;
                            }

                            const colIndex = typeof col === "string" ? hot.propToCol(col) : col;
                            const monthIndex = colIndex - rowHeaderCols;

                            changedData.push([row, monthIndex, newVal]);
                            colsToSummarize.add(colIndex);
                            rowsToSummarize.add(row);
                        }

                        const updatesToMake: [number, number, (number | null)][] = [];

                        for (const colToSummarize of Array.from(colsToSummarize)) {
                            const summaryRows = data.filter(x => x.rowType == "summary");

                            for (const summaryRow of summaryRows) {
                                const unitTypeValuesToSummarize: number[] = data.filter(x => x.parentRowId === summaryRow.rowId).map(x => x.values[colToSummarize - rowHeaderCols] ?? 0);
                                const summaryRowIndex = data.indexOf(summaryRow);
                                const summaryFunction = summaryRow.parentRowSummaryFunction ?? tableSummaryFunction;
                                updatesToMake.push([summaryRowIndex, colToSummarize, getSummaryValue(summaryRowIndex, colToSummarize - rowHeaderCols, unitTypeValuesToSummarize, summaryFunction)]);
                            }
                        }

                        if (summaryColumns) {
                            for (const rowToSummarize of Array.from(rowsToSummarize)) {
                                const rowValues = hot.getDataAtRow(rowToSummarize).slice(rowHeaderCols, endCol);

                                for (const summaryColumn of summaryColumns) {
                                    const newSummaryValue = getSummaryValue(rowToSummarize, summaryColumn.col, rowValues, summaryColumn.summaryFunction);
                                    updatesToMake.push([rowToSummarize, summaryColumn.col, newSummaryValue]);
                                }
                            }
                        }

                        hot.setDataAtCell(updatesToMake, "computedColSummary");

                        for (const [flashRow, flashCol] of updatesToMake) {
                            hot.setCellMeta(flashRow, flashCol, "className", "cell-flash");
                        }

                        onChangeColorFlashTimeout.current = setTimeout(() => {
                            if (!hot || hot.isDestroyed) {
                                return;
                            }

                            for (const [flashRow, flashCol] of updatesToMake) {
                                if (hot.getCell(flashRow, flashCol) == null) {
                                    continue;
                                }
                                hot.removeCellMeta(flashRow, flashCol, "className");
                            }
                            hot.render();
                        }, 1000);

                        handleOnChange(changedData);

                        // when the user makes a new manual input, the redo state should get cleared
                        if (!source.includes("UndoRedo")) {
                            setCanRedo(false);
                        }
                    }

                    // once parent columns have been summarized, perform parent row summary
                    if (calculationInputSources.includes(source)) {
                        const summaryRows = data.filter(x => x.rowType == "summary");

                        for (const summaryRow of summaryRows) {
                            const summaryRowIndex = data.indexOf(summaryRow);
                            const rowValues = hot.getDataAtRow(summaryRowIndex).slice(rowHeaderCols, endCol);
                            const summaryFunction = summaryRow.rowSummaryFunction ?? tableSummaryFunction;
                            const newSummaryValue = getSummaryValue(summaryRowIndex, endCol, rowValues, summaryFunction);

                            hot.setDataAtCell(summaryRowIndex, endCol, newSummaryValue, "computedRowSummary");
                            hot.setCellMeta(summaryRowIndex, endCol, "className", "cell-flash");

                            onChangeColorFlashTimeout.current = setTimeout(() => {
                                if (!hot || hot.isDestroyed || hot.getCell(summaryRowIndex, endCol) == null) {
                                    return;
                                }
                                hot.removeCellMeta(summaryRowIndex, endCol, "className");
                                hot.render();
                            }, 1000);
                        }
                    }
                });
            },
            afterUndo: () => {
                setCanUndo(hotRef.current?.hotInstance?.isUndoAvailable() ?? false);
                setCanRedo(true);
            },
            afterRedo: () => {
                setCanUndo(true);
                setCanRedo(hotRef.current?.hotInstance?.isRedoAvailable() ?? false);
            },
            beforeUndoStackChange: (_doneActions, _source) => {
                const source: string = _source as string;

                if (undoRedoSkipSources.includes(source)) {
                    return false;
                }
            },
            afterUndoStackChange: (_before, after) => {
                setCanUndo(after.length > 0);
            },
            ...handsontableProps,
        };
    }, [data, hiddenRows]);

    return (
        <div className="vz-hot-table has-min-height">
            {!isDataLoading && data?.length == 0 &&
                <VZAlertNoUnitTypes userIsAdmin={userIsAdmin(user)} />
            }

            <VZHotTableControls
                canUndo={canUndo}
                canRedo={canRedo}
                isDataLoading={isDataLoading ?? false}
                isSearchable={isSearchable ?? false}
                isUndoRedoable={isUndoRedoable ?? false}
                canSuppressZeroes={canSuppressZeroes ?? false}
                onUndo={() => hotRef.current?.hotInstance?.undo()}
                onRedo={() => hotRef.current?.hotInstance?.redo()}
                onFilterTextChange={(text) => setFilterText(text)}
                onSuppressZeroes={setSuppressZeroes}
                customControls={customControls}
            />
            <HotTable {...compiledTableProps} />
        </div>
    );
}
